import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ShieldAlert } from "lucide-react";

import Button from "../../../../../components/buttons/Button";
import SelectField from "../../../../../components/input/select/Select";

import { useMemberGroups } from "../../hooks/useMemberGroups";

import "./MemberJoinGroupModal.css";

const JOIN_REASON_OPTIONS = [
  { label: "Player", value: "Player" },
  { label: "Coach", value: "Coach" },
  { label: "Scout", value: "Scout" },
  { label: "Family", value: "Family" },
  { label: "Fan", value: "Fan" },
  { label: "Media", value: "Media" },
  { label: "Other", value: "Other" },
];

const MemberJoinGroupModal = ({ open, group, userId, onClose, onSuccess }) => {
  const { joinGroup, loading } = useMemberGroups(userId);

  const [reasonForJoining, setReasonForJoining] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const selectedGroup = group?.group || group;

  const groupTag = selectedGroup?.groupTag;
  const groupName = selectedGroup?.groupName;
  const groupCode = selectedGroup?.groupCode;
  const groupPolicy = selectedGroup?.groupPolicy;

  const isSubmitting = loading || submitting;

  useEffect(() => {
    if (!open) return;

    setReasonForJoining("");
    setError("");
  }, [open, groupTag]);

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !isSubmitting) {
        onClose?.();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, isSubmitting, onClose]);

  const handleReasonChange = (event) => {
    const value =
      typeof event === "string"
        ? event
        : (event?.target?.value ?? event?.value ?? "");

    setReasonForJoining(String(value));
    setError("");
  };

  const handleClose = () => {
    if (isSubmitting) return;

    setReasonForJoining("");
    setError("");

    onClose?.();
  };

  const handleJoin = async () => {
    if (isSubmitting) return;

    const reason = reasonForJoining.trim();

    if (!reason) {
      setError("Please select your reason for joining.");
      return;
    }

    if (!groupTag) {
      setError("Invalid group. Please try again.");
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      const result = await joinGroup(groupTag, reason);

      if (!result || result?.success === false) {
        setError(
          result?.message || "Unable to join this group. Please try again.",
        );

        return;
      }

      onSuccess?.(result);

      setReasonForJoining("");
      onClose?.();
    } catch (requestError) {
      console.error("MemberJoinGroupModal join failed:", requestError);

      setError(
        requestError?.response?.data?.message ||
          requestError?.message ||
          "Unable to join this group. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div
      className="member-join-group"
      role="dialog"
      aria-modal="true"
      aria-labelledby="member-join-group-title"
    >
      <div
        className="member-join-group__overlay"
        onClick={handleClose}
        aria-hidden="true"
      />

      <div className="member-join-group__container">
        <header className="member-join-group__header">
          <div className="member-join-group__identity">
            <div className="member-join-group__identity-content">
              <p className="member-join-group__eyebrow">Request access to</p>

              <h2
                id="member-join-group-title"
                className="member-join-group__title"
              >
                {groupName || "Group"}
              </h2>
            </div>

            <span className="member-join-group__code">{groupCode || "—"}</span>
          </div>
        </header>

        <div className="member-join-group__body">
          <div className="member-join-group__notice">
            <ShieldAlert
              size={20}
              className="member-join-group__notice-icon"
              aria-hidden="true"
            />

            <div className="member-join-group__notice-content">
              <h3 className="member-join-group__notice-title">
                Group Privacy &amp; Rules
              </h3>

              <p className="member-join-group__notice-text">
                Review the access rules before joining.
              </p>
            </div>
          </div>

          <p className="member-join-group__policy">
            {groupPolicy ||
              "By joining, you agree to use this group responsibly."}
          </p>

          <div className="member-join-group__field">
            <SelectField
              label="Reason for joining?"
              name="reasonForJoining"
              value={reasonForJoining}
              onChange={handleReasonChange}
              items={JOIN_REASON_OPTIONS}
              disabled={isSubmitting}
              required
            />
          </div>

          {error && (
            <p
              className="member-join-group__error"
              role="alert"
              style={{
                color: "var(--ko-danger, #ef4444)",
                fontSize: "13px",
                margin: "8px 0 0",
              }}
            >
              {error}
            </p>
          )}
        </div>

        <footer className="member-join-group__footer">
          <Button
            type="button"
            variant="primary-outlined"
            value="Cancel"
            disabled={isSubmitting}
            onClick={handleClose}
          />

          <Button
            type="button"
            value={isSubmitting ? "Joining..." : "Join Group"}
            disabled={isSubmitting || !reasonForJoining.trim()}
            onClick={handleJoin}
          />
        </footer>
      </div>
    </div>,
    document.body,
  );
};

export default MemberJoinGroupModal;
